import {BaseElement, BASIS} from "back/commons/basis";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {BaseAreaViewAsync, IView, IViewApi, IViewsContainer, OBaseAreaViewInit, OViewVisitOptions, VIEWS} from "lib/commons/views";
import {JSX} from "lib/commons/xml/dom";
import {IDepotResUiEnv, IDepotShortResUiEnv} from "lib/store/depot";
import {IArea, isArea} from "lib/commons/areas";
import {JLastDatas} from "lib/commons/lastDatas";

/**
 * Mise en page d'une view des ressources imbriquant un arbre de :
 * - IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView> de la ressource,
 * - des tags html ou customElements free (avec chargement dynamique d'une lib possible),
 * - des noeuds textes.
 */
export interface ResLayoutView extends BaseAreaViewAsync<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>> {
	initialize(init: OResLayoutViewInit): this;
}

export interface OResLayoutViewInit extends OBaseAreaViewInit<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>> {
	reg: IReg<IDepotResUiEnv | IDepotShortResUiEnv>
	scroll?: 'small' | 'large'
	body: (IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView> | IJsTag | string)[]

	/** Si true, le body est construit en fils de ce tag, pas dans son shadow tree. */
	noShadow?: boolean
}

/** Définition d'un tag html en json. */
export interface IJsTag {
	/** Nom du tag (html standard ou customElement). */
	tag: string

	/** path de la lib à charger pour charger le customElement*/
	lib?: string

	/** Liste des noeuds fils. */
	children?: (IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView> | IJsTag | string)[]

	/** Attributs du tag. */
	atts?: Dict<string>

	/** Objet d'init du tag cf IEltInitable. */
	init?: object | ((ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>) => any)
}

export class ResLayoutView extends BaseAreaViewAsync<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>> implements IViewsContainer {

	static loadLibs(views: Array<IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView> | IJsTag | string>, ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, promises: Promise<any>[]) {
		for (let v of views) {
			if (v == null) continue;
			if (isArea(v)) {
				if (v.needAsync(ctx)) promises.push(v.loadLibs(ctx));
			} else if (typeof v === "object") {
				if (v.children) ResLayoutView.loadLibs(v.children, ctx, promises);
				if (v.lib) promises.push(ctx.reg.env.resolver.importJs(v.lib));
			}
		}
	}

	static buildSrcView(v: IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView> | IJsTag | string, parent: Node, ctx: IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, lastDatas: JLastDatas) {
		if (!v) return;
		if (typeof v === 'string') parent.appendChild(document.createTextNode(v));
		else if (isArea(v)) {
			parent.appendChild(v.buildBody(ctx, lastDatas));
		} else {
			const tag = document.createElement(v.tag);
			const atts = v.atts;
			if (atts) for (const att in atts) tag.setAttribute(att, atts[att]);
			if (tag instanceof BaseElement) {
				tag.initialize(typeof v.init === 'function' ? v.init(ctx) : BASIS.newInit(v.init, ctx.reg));
				//if (isEltInitableAsyncPending(tag)) await tag.initializedAsync; utile ?
			}
			parent.appendChild(tag);
			if (v.children) for (const subV of v.children) {
				ResLayoutView.buildSrcView(subV, tag, ctx, lastDatas);
			}
		}
	}

	reg: IReg<IDepotResUiEnv | IDepotShortResUiEnv>;

	body: (IArea<IRegPointer<IDepotResUiEnv | IDepotShortResUiEnv>, IView> | IJsTag | string)[];

	protected _lastDatas: JLastDatas | null | undefined;

	_initialize(init: OResLayoutViewInit) {
		super._initialize(init);
		if (!init.noShadow) {
			this._attach(this.localName, init);
			if (init.scroll) {
				this.reg.installSkin(init.scroll == "large" ? "scroll/large" : "scroll/small", this.shadowRoot);
				this.style.overflow = "auto";
			}
		}
		this.body = init.body;
		this._lastDatas = init.lastDatas || null;
		const loading: Promise<any>[] = [];
		ResLayoutView.loadLibs(this.body, this.reg, loading);
		return Promise.all(loading) as Promise<any>;
	}

	visitViews(visitor: (view: IViewApi) => any, options?: OViewVisitOptions): any {
		return VIEWS.visitDescendants(this.shadowRoot || this, visitor);
	}

	async visitViewsAsync(visitor: (view: IViewApi) => Promise<any>, options?: OViewVisitOptions): Promise<any> {
		return VIEWS.visitDescendantsAsync(this.shadowRoot || this, visitor);
	}

	protected _refresh() {
		const lastDatas = this._lastDatas;
		if (lastDatas !== undefined) {
			//1ère construction
			this._lastDatas = undefined;
			if (this.reg.isClosed) return; //racecond issu de l'initialize async
			const root = this.shadowRoot || this;
			for (const v of this.body) {
				ResLayoutView.buildSrcView(v, root, this.reg, lastDatas);
			}
		} else {
			if (this.shadowRoot) {
				BASIS.refreshTree(this.shadowRoot);
			} else {
				BASIS.refreshTree(this, true);
			}
		}
	}
}

REG.reg.registerSkin("store-res-layout", 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
	}

	[hidden] {
		display: none;
	}
`);

customElements.define("store-res-layout", ResLayoutView);